import {
  setCookie,
  getStandardErrorObject,
  getTransactionId,
  logError,
  logWarn,
  getSyncUrl,
  getLoggingHeaders,
  fetchWithTimeout,
  trimStack,
} from './Utils.js';
import { ClientErrors } from './ClientErrors.js';

// The map of tokens we receive from the SSO tokens endpoints
let tokenData = {};

/**
 * Describe a rejected fetch for logging.
 *
 * Error objects keep name/message/stack/cause as non-enumerable own properties,
 * so spreading one collapses to `{}` and throws away everything useful. Spread
 * first to keep any enumerable metadata a non-TypeError rejection carries, then
 * set the standard fields explicitly so they win, and attach the signals that
 * tell the failure modes apart: a navigation abort, an offline browser, an
 * extension-blocked request and a CORS rejection all surface to JS as the same
 * opaque TypeError.
 */
function describeFetchFailure(e, url, startedAt) {
  const isObject = typeof e === 'object' && e !== null;
  const detail = {
    // Spreading a string would spread it per character, hence the guard.
    ...(isObject ? e : {}),
    ...(isObject && typeof e.response === 'object' && e.response !== null
      ? e.response
      : {}),
    name: e?.name,
    message: e?.message,
    stack: trimStack(e?.stack),
    cause: e?.cause,
    url,
    elapsedMs: Date.now() - startedAt,
    // Keep the raw value when something non-object was thrown, since the
    // spread above cannot represent it.
    ...(isObject ? {} : { thrown: e }),
  };
  try {
    detail.online = navigator.onLine;
    detail.visibility = document.visibilityState;
    // A CORS-rejected request still goes out on the wire and lands in the
    // resource timing buffer; one that an extension blocks outright usually
    // never gets there. This is the closest thing to a CORS-vs-blocked signal
    // the browser gives us.
    detail.hasTimingEntry = performance.getEntriesByName(url).length > 0;
  } catch {
    // Diagnostics are best effort — never let them hide the original error.
  }
  return detail;
}

export class MyStorage {
  static getTokenData() {
    return tokenData;
  }

  static setItem(key, value) {
    tokenData[key] = value;
    return tokenData[key];
  }

  static getItem(key) {
    return tokenData[key];
  }

  static removeItem(key) {
    delete tokenData[key];
  }

  static clear() {
    tokenData = {};
    MyStorage.tokenSyncPromise = null;
  }

  static sync() {
    if (!MyStorage.tokenSyncPromise) {
      MyStorage.tokenSyncPromise = new Promise((resolve, reject) => {
        const syncUrl = getSyncUrl();
        const startedAt = Date.now();
        fetchWithTimeout(syncUrl, {
          credentials: 'include',
          headers: {
            'content-type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
            ...getLoggingHeaders(),
          },
        })
          .then((result) => {
            if (result.status === 200) {
              result
                .json()
                .then((data) => {
                  // Purge stale tokens only once a 200 sync succeeds, so a failed
                  // or empty sync doesn't wipe the tokens we already have. Only the
                  // token data is reset: clear() would also drop the cached sync
                  // promise, making every later sync() refetch.
                  tokenData = {};
                  data.forEach((token) => {
                    // We set the persistUserLogin cookie in the client domain for legacy purposes on shutterfly.com and tinyprints.com
                    // TODO: Do we really need to set this for www.shutterfly.com after we decouple the HTTP session? Check After Q4 2020.
                    if (token.name === 'persistUserLogin') {
                      const d = new Date();
                      if (token.value === 'off') {
                        // Update from 24 hours to 7 days
                        d.setTime(d.getTime() + 7 * 24 * 60 * 60 * 1000);
                      } else {
                        // 450 days (this is what is in the SPA)
                        d.setTime(d.getTime() + 450 * 24 * 60 * 60 * 1000);
                      }
                      let host = window.location.hostname;
                      const match = host.match(/(dev|beta|stage|kappa)/i);
                      if (match) {
                        host = host.substring(match.index);
                      } else if (host.indexOf('.shutterfly.com')) {
                        host = '.shutterfly.com';
                      } else if (host.indexOf('.tinyprints.com')) {
                        host = '.tinyprints.com';
                      }
                      setCookie(token.name, token.value, d, host);
                    }
                    MyStorage.setItem(
                      decodeURIComponent(token.name),
                      decodeURIComponent(token.value),
                    );
                  });
                  resolve();
                })
                .catch((e) => {
                  // A 200 with an unparseable body would otherwise leave the promise
                  // pending forever; fail it so callers don't hang.
                  const transId = getTransactionId();
                  logError(
                    ClientErrors.TOKEN_SYNC,
                    {
                      name: e?.name,
                      message: e?.message,
                      stack: trimStack(e?.stack),
                      status: result.status,
                      url: result.url,
                    },
                    transId,
                  );
                  const err = new Error(ClientErrors.TOKEN_SYNC.message);
                  Object.assign(
                    err,
                    getStandardErrorObject(ClientErrors.TOKEN_SYNC, transId),
                  );
                  reject(err);
                });
            }
            if (result.status !== 200) {
              // 204 means the user is logged out server side, so drop the stale
              // tokens. Other statuses are server errors; keep what we have.
              if (result.status === 204) {
                tokenData = {};
              }
              const transId = getTransactionId();
              // A 204 is an expected logged-out state, not a failure, so it is
              // logged as a warning to keep it out of error counts.
              const is204 = result.status === 204;
              (is204 ? logWarn : logError)(
                is204
                  ? ClientErrors.TOKEN_SYNC_NO_CONTENT
                  : ClientErrors.TOKEN_SYNC_HTTP_ERROR,
                {
                  status: result.status,
                  statusText: result.statusText,
                  url: result.url,
                },
                transId,
              );
              resolve();
            }
          })
          .catch((e) => {
            // Get a transId to link errors from the client to splunk
            const transId = getTransactionId();
            logError(
              ClientErrors.TOKEN_SYNC,
              describeFetchFailure(e, syncUrl, startedAt),
              transId,
            );
            const err = new Error(ClientErrors.TOKEN_SYNC.message);
            Object.assign(
              err,
              getStandardErrorObject(ClientErrors.TOKEN_SYNC, transId),
            );
            reject(err);
          });
      });
    }
    return MyStorage.tokenSyncPromise;
  }
}
